import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

function pickOrgHeaders() {
  // Org selector not yet wired globally; keep hook for future use.
  return {};
}

function asText(v) {
  return v == null ? '' : String(v);
}

function trimOrEmpty(v) {
  return String(v || '').trim();
}

function nowIsoDate() {
  try {
    return new Date().toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function portalToBody(node) {
  try {
    if (typeof document !== 'undefined' && document && document.body) return createPortal(node, document.body);
  } catch {}
  return node;
}

export default function ModuleTodosTable({
  moduleId,
  title = 'Todo list',
  all = false,
  compact = false,
  defaultIncludeDone = false,
  maxHeight = compact ? 160 : 420,
}) {
  const safeModuleId = trimOrEmpty(moduleId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [includeDone, setIncludeDone] = useState(!!defaultIncludeDone);
  const [q, setQ] = useState('');
  const [filterModuleId, setFilterModuleId] = useState('');

  const [newTitle, setNewTitle] = useState('');
  const [newModuleId, setNewModuleId] = useState(safeModuleId);

  const [editing, setEditing] = useState(null); // todo row
  const [editTitle, setEditTitle] = useState('');
  const [editDone, setEditDone] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesBusy, setFilesBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  const openCount = useMemo(() => (items || []).filter((x) => !x.done).length, [items]);
  const [compactOpen, setCompactOpen] = useState(false);
  const showFull = !compact;
  const compactPreview = useMemo(() => {
    if (!compact) return '';
    try {
      const titles = (items || [])
        .filter((x) => !x.done)
        .slice(0, 3)
        .map((x) => trimOrEmpty(x.title))
        .filter(Boolean);
      return titles.join(' • ');
    } catch {
      return '';
    }
  }, [compact, items]);

  async function load() {
    if (!all && !safeModuleId) return;
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      qs.set('limit', compact ? '80' : '200');
      if (includeDone) qs.set('include_done', '1');
      if (q) qs.set('q', q);
      if (all) {
        if (trimOrEmpty(filterModuleId)) qs.set('module_id', trimOrEmpty(filterModuleId));
      } else {
        qs.set('module_id', safeModuleId);
      }
      const endpoint = all ? '/api/module-manager/todos/all' : '/api/module-manager/todos';
      const r = await fetch(`${endpoint}?${qs.toString()}`, { credentials: 'include', headers: pickOrgHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) throw new Error(j?.message || j?.error || `HTTP_${r.status}`);
      setItems(Array.isArray(j.items) ? j.items : []);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  async function createTodo() {
    const t = trimOrEmpty(newTitle);
    if (!t) return;
    const mid = all ? trimOrEmpty(newModuleId) : safeModuleId;
    if (!mid) {
      setError('Missing module id');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const r = await fetch('/api/module-manager/todos', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...pickOrgHeaders() },
        body: JSON.stringify({ module_id: mid, title: t, done: false, position: 0 }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) throw new Error(j?.message || j?.error || `HTTP_${r.status}`);
      setNewTitle('');
      if (all) setNewModuleId(mid);
      await load();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function updateTodo(id, patch) {
    if (!id) return;
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`/api/module-manager/todos/${encodeURIComponent(String(id))}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...pickOrgHeaders() },
        body: JSON.stringify(patch || {}),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) throw new Error(j?.message || j?.error || `HTTP_${r.status}`);
      await load();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function deleteTodo(id) {
    if (!id) return;
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`/api/module-manager/todos/${encodeURIComponent(String(id))}`, { method: 'DELETE', credentials: 'include', headers: pickOrgHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) throw new Error(j?.message || j?.error || `HTTP_${r.status}`);
      await load();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function loadFiles(todoId) {
    if (!todoId) return;
    setFilesBusy(true);
    try {
      const r = await fetch(`/api/module-manager/todos/${encodeURIComponent(String(todoId))}/files`, { credentials: 'include', headers: pickOrgHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) throw new Error(j?.message || j?.error || `HTTP_${r.status}`);
      setFiles(Array.isArray(j.items) ? j.items : []);
    } catch (e) {
      setError(String(e?.message || e));
      setFiles([]);
    } finally {
      setFilesBusy(false);
    }
  }

  function openEdit(it) {
    if (!it) return;
    setEditing(it);
    setEditTitle(asText(it.title));
    setEditDone(!!it.done);
    setFiles([]);
    loadFiles(it.id);
  }

  async function saveEdit() {
    if (!editing || !editing.id) return;
    const patch = {};
    const t = trimOrEmpty(editTitle);
    if (t && t !== trimOrEmpty(editing.title)) patch.title = t;
    if (!!editDone !== !!editing.done) patch.done = !!editDone;
    if (Object.keys(patch).length === 0) return;
    await updateTodo(editing.id, patch);
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('read_failed'));
        reader.onload = () => {
          try {
            const s = String(reader.result || '');
            const m = s.match(/^data:([^;]+);base64,(.*)$/);
            if (m) return resolve({ mime: m[1], b64: m[2] });
            return resolve({ mime: file.type || null, b64: '' });
          } catch (e) {
            reject(e);
          }
        };
        reader.readAsDataURL(file);
      } catch (e) {
        reject(e);
      }
    });
  }

  async function uploadFile(file) {
    if (!editing || !editing.id || !file) return;
    setUploadBusy(true);
    setError('');
    try {
      const { mime, b64 } = await fileToBase64(file);
      if (!b64) throw new Error('empty_file');
      const r = await fetch(`/api/module-manager/todos/${encodeURIComponent(String(editing.id))}/files`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...pickOrgHeaders() },
        body: JSON.stringify({ filename: file.name, mime: mime || null, content_b64: b64 }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) throw new Error(j?.message || j?.error || `HTTP_${r.status}`);
      await loadFiles(editing.id);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setUploadBusy(false);
    }
  }

  async function deleteFile(fileId) {
    if (!fileId) return;
    setUploadBusy(true);
    setError('');
    try {
      const r = await fetch(`/api/module-manager/todos/files/${encodeURIComponent(String(fileId))}`, { method: 'DELETE', credentials: 'include', headers: pickOrgHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) throw new Error(j?.message || j?.error || `HTTP_${r.status}`);
      if (editing && editing.id) await loadFiles(editing.id);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setUploadBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeModuleId, includeDone, all, q, filterModuleId]);

  if (!all && !safeModuleId) return null;

  return (
    <>
      {compact ? (
        <div className="rounded border bg-white w-full">
          <div className="flex items-center justify-between gap-2 px-3 py-2">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-slate-800 truncate">
                {title}{' '}
                <span className="ml-2 text-[11px] font-normal text-slate-500">({openCount} open)</span>
              </div>
              {compactPreview ? (
                <div className="text-[11px] text-slate-600 truncate" title={compactPreview}>
                  {compactPreview}
                </div>
              ) : null}
              {error ? <div className="text-[11px] text-red-600 truncate">{error}</div> : null}
            </div>
            <div className="flex items-center gap-2 flex-none">
              <button className="rounded border px-2 py-1 text-xs" onClick={load} disabled={loading || saving}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
              <button className="rounded border px-2 py-1 text-xs" onClick={() => setCompactOpen(true)} disabled={loading || saving}>
                Open
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded border bg-white">
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50">
            <div className="text-sm font-semibold text-slate-800">
              {title} <span className="ml-2 text-xs font-normal text-slate-500">({openCount} open)</span>
            </div>
            <div className="flex items-center gap-2">
              {all ? (
                <input
                  className="w-40 rounded border px-2 py-1 text-xs"
                  value={filterModuleId}
                  onChange={(e) => setFilterModuleId(e.target.value)}
                  placeholder="module id…"
                  title="Filter by module id"
                />
              ) : null}
              <input
                className="w-44 rounded border px-2 py-1 text-xs"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="search…"
                title="Search title"
              />
              <label className="text-xs text-slate-600 inline-flex items-center gap-2">
                <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
                Show done
              </label>
              <button className="rounded border px-2 py-1 text-xs" onClick={load} disabled={loading || saving}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>

          {error ? <div className="px-3 py-2 text-xs text-red-600 border-b">{error}</div> : null}

          {showFull ? (
            <div className="px-3 py-2 border-b">
              <div className="grid grid-cols-1 md:grid-cols-[1fr,1.5fr,auto] gap-2 items-end">
                {all ? (
                  <div>
                    <div className="text-[11px] text-slate-500 mb-1">Module id</div>
                    <input className="w-full rounded border px-2 py-1 text-sm" value={newModuleId} onChange={(e) => setNewModuleId(e.target.value)} placeholder="supply-planification" />
                  </div>
                ) : null}
                <div>
                  <div className="text-[11px] text-slate-500 mb-1">Title</div>
                  <input className="w-full rounded border px-2 py-1 text-sm" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder={`New todo (${nowIsoDate()})…`} />
                </div>
                <button className="rounded bg-black text-white px-3 py-2 text-sm" onClick={createTodo} disabled={saving || loading || !trimOrEmpty(newTitle)}>
                  Add
                </button>
              </div>
            </div>
          ) : null}

          <div className="overflow-auto" style={{ maxHeight }}>
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white border-b">
                <tr className="text-xs text-slate-500">
                  {all ? <th className="text-left px-3 py-2 w-44">Module</th> : null}
                  <th className="text-left px-3 py-2 w-14">Done</th>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-left px-3 py-2 w-36">Updated</th>
                  <th className="text-left px-3 py-2 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(items || []).map((it) => (
                  <tr key={it.id} className="border-b last:border-b-0">
                    {all ? <td className="px-3 py-2 font-mono text-xs text-slate-700">{asText(it.module_id)}</td> : null}
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={!!it.done} onChange={(e) => updateTodo(it.id, { done: e.target.checked })} disabled={saving} />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className="w-full rounded border px-2 py-1 text-sm"
                        value={asText(it.title)}
                        onChange={(e) => setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, title: e.target.value } : x)))}
                        onBlur={(e) => {
                          const v = trimOrEmpty(e.target.value);
                          if (v && v !== trimOrEmpty(it.title)) updateTodo(it.id, { title: v });
                        }}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{asText(it.updated_at)}</td>
                    <td className="px-3 py-2">
                      <button className="rounded border px-2 py-1 text-xs mr-2" onClick={() => openEdit(it)} disabled={saving}>
                        Edit
                      </button>
                      <button className="rounded border px-2 py-1 text-xs" onClick={() => deleteTodo(it.id)} disabled={saving}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {(!items || !items.length) && (
                  <tr>
                    <td className="px-3 py-4 text-sm text-slate-500" colSpan={all ? 5 : 4}>
                      No todos.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing ? portalToBody(
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl border" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
              <div className="text-sm font-semibold text-slate-900">
                Todo {all ? `(${asText(editing.module_id)})` : ''} — #{asText(editing.id)}
              </div>
              <div className="flex items-center gap-2">
                <button className="rounded border px-2 py-1 text-xs" onClick={() => setEditing(null)} disabled={saving || uploadBusy}>
                  Close
                </button>
              </div>
            </div>
            <div className="p-4 grid grid-cols-1 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-sm inline-flex items-center gap-2">
                    <input type="checkbox" checked={editDone} onChange={(e) => setEditDone(e.target.checked)} />
                    Done
                  </label>
                  <button className="rounded bg-black text-white px-3 py-1.5 text-sm" onClick={saveEdit} disabled={saving}>
                    Save
                  </button>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 mb-1">Title</div>
                  <input className="w-full rounded border px-2 py-1 text-sm" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                </div>

                <div className="rounded border p-3">
                  <div className="text-sm font-semibold text-slate-800 mb-2">Attachments</div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="file"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        if (f) uploadFile(f);
                        try { e.target.value = ''; } catch {}
                      }}
                      disabled={uploadBusy}
                    />
                    {uploadBusy ? <span className="text-xs text-slate-500">Uploading…</span> : null}
                    {filesBusy ? <span className="text-xs text-slate-500">Loading…</span> : null}
                  </div>
                  <div className="overflow-auto max-h-40">
                    <table className="min-w-full text-xs">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="text-left py-1 pr-2">File</th>
                          <th className="text-left py-1 pr-2 w-24">Size</th>
                          <th className="text-left py-1 pr-2 w-36">Created</th>
                          <th className="text-left py-1 pr-2 w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(files || []).map((f) => (
                          <tr key={f.id} className="border-t">
                            <td className="py-1 pr-2">
                              <a className="text-blue-700 underline" href={`/api/module-manager/todos/files/${encodeURIComponent(String(f.id))}`} target="_blank" rel="noreferrer">
                                {asText(f.filename)}
                              </a>
                            </td>
                            <td className="py-1 pr-2 text-slate-600">{asText(f.size_bytes)}</td>
                            <td className="py-1 pr-2 text-slate-600">{asText(f.created_at)}</td>
                            <td className="py-1 pr-2">
                              <button className="rounded border px-2 py-0.5" onClick={() => deleteFile(f.id)} disabled={uploadBusy}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                        {(!files || !files.length) ? (
                          <tr>
                            <td className="py-2 text-slate-500" colSpan={4}>No files.</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
      ) : null}
		
	    {compact && compactOpen ? portalToBody(
	      <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 p-4" onClick={() => setCompactOpen(false)}>
	        <div className="w-full max-w-6xl rounded-lg bg-white shadow-xl border overflow-hidden" onClick={(e) => e.stopPropagation()}>
	          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50">
	            <div className="text-sm font-semibold text-slate-800">
	              {title} <span className="ml-2 text-xs font-normal text-slate-500">({openCount} open)</span>
	            </div>
	            <div className="flex items-center gap-2">
	              <input
	                className="w-56 rounded border px-2 py-1 text-xs"
	                value={q}
	                onChange={(e) => setQ(e.target.value)}
	                placeholder="search…"
	                title="Search title"
	              />
	              <label className="text-xs text-slate-600 inline-flex items-center gap-2">
	                <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
	                Show done
	              </label>
	              <button className="rounded border px-2 py-1 text-xs" onClick={load} disabled={loading || saving}>
	                {loading ? 'Loading…' : 'Refresh'}
	              </button>
	              <button className="rounded border px-2 py-1 text-xs" onClick={() => setCompactOpen(false)}>Close</button>
	            </div>
	          </div>
	          <div className="p-3">
	            <div className="text-xs text-slate-600 mb-2">Use the inline “Edit” button to manage attachments.</div>
	            <div className="rounded border overflow-hidden">
	              {/* Re-render the component body in expanded mode by showing full UI inside the modal */}
	              <div className="px-3 py-2 border-b">
	                <div className="grid grid-cols-1 md:grid-cols-[1fr,1.5fr,auto] gap-2 items-end">
	                  {all ? (
	                    <div>
	                      <div className="text-[11px] text-slate-500 mb-1">Module id</div>
	                      <input className="w-full rounded border px-2 py-1 text-sm" value={newModuleId} onChange={(e) => setNewModuleId(e.target.value)} placeholder="supply-planification" />
	                    </div>
	                  ) : null}
	                  <div>
	                    <div className="text-[11px] text-slate-500 mb-1">Title</div>
	                    <input className="w-full rounded border px-2 py-1 text-sm" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder={`New todo (${nowIsoDate()})…`} />
	                  </div>
	                  <button className="rounded bg-black text-white px-3 py-2 text-sm" onClick={createTodo} disabled={saving || loading || !trimOrEmpty(newTitle)}>
	                    Add
	                  </button>
	                </div>
	              </div>
	              <div className="overflow-auto" style={{ maxHeight: 520 }}>
	                <table className="min-w-full text-sm">
	                  <thead className="sticky top-0 bg-white border-b">
	                    <tr className="text-xs text-slate-500">
	                      {all ? <th className="text-left px-3 py-2 w-44">Module</th> : null}
	                      <th className="text-left px-3 py-2 w-14">Done</th>
	                      <th className="text-left px-3 py-2">Title</th>
	                      <th className="text-left px-3 py-2 w-36">Updated</th>
	                      <th className="text-left px-3 py-2 w-20">Actions</th>
	                    </tr>
	                  </thead>
	                  <tbody>
	                    {(items || []).map((it) => (
	                      <tr key={it.id} className="border-b last:border-b-0">
	                        {all ? <td className="px-3 py-2 font-mono text-xs text-slate-700">{asText(it.module_id)}</td> : null}
	                        <td className="px-3 py-2">
	                          <input type="checkbox" checked={!!it.done} onChange={(e) => updateTodo(it.id, { done: e.target.checked })} disabled={saving} />
	                        </td>
	                        <td className="px-3 py-2">
	                          <input
	                            className="w-full rounded border px-2 py-1 text-sm"
	                            value={asText(it.title)}
	                            onChange={(e) => setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, title: e.target.value } : x)))}
	                            onBlur={(e) => {
	                              const v = trimOrEmpty(e.target.value);
	                              if (v && v !== trimOrEmpty(it.title)) updateTodo(it.id, { title: v });
	                            }}
	                            disabled={saving}
	                          />
	                        </td>
	                        <td className="px-3 py-2 text-xs text-slate-600">{asText(it.updated_at)}</td>
	                        <td className="px-3 py-2">
	                          <button className="rounded border px-2 py-1 text-xs mr-2" onClick={() => openEdit(it)} disabled={saving}>
	                            Edit
	                          </button>
	                          <button className="rounded border px-2 py-1 text-xs" onClick={() => deleteTodo(it.id)} disabled={saving}>
	                            Delete
	                          </button>
	                        </td>
	                      </tr>
	                    ))}
	                    {(!items || !items.length) && (
	                      <tr><td className="px-3 py-4 text-sm text-slate-500" colSpan={all ? 5 : 4}>No todos.</td></tr>
	                    )}
	                  </tbody>
	                </table>
	              </div>
	            </div>
	          </div>
	        </div>
	      </div>,
	    ) : null}
    </>
  );
}
